import { useState, useEffect } from 'react';
import axios from 'axios';
import { Settings, User, Shield, Bell, Sliders, Loader2, Save } from 'lucide-react';
import { toast } from 'react-hot-toast';

interface UserSettings {
  display_name?: string;
  email?: string;
  bio?: string;
  theme?: 'dark' | 'light';
  language?: string;
  email_notifications?: boolean;
  marketing_emails?: boolean;
  two_factor_enabled?: boolean;
}

export default function UserSettingsOSPage() {
  const [activeTab, setActiveTab] = useState<'profile' | 'security' | 'notifications' | 'preferences'>('profile');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<UserSettings>({});

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${import.meta.env.VITE_API_URL}/api/user-panel/settings`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
        });
        const data = res.data;
        setFormData({ ...data.profile, ...data.preferences });
      } catch (err) {
        console.error(err);
        toast.error('Ayarlar yüklenemedi.');
      } finally {
        setLoading(false);
      }
    };
    void fetchSettings();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${import.meta.env.VITE_API_URL}/api/user-panel/settings`, {
        display_name: formData.display_name,
        bio: formData.bio,
        preferences: {
          theme: formData.theme,
          language: formData.language,
          email_notifications: formData.email_notifications,
          marketing_emails: formData.marketing_emails,
          two_factor_enabled: formData.two_factor_enabled
        }
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
      });
      toast.success('Ayarlar başarıyla kaydedildi.');
    } catch (err) {
      console.error(err);
      toast.error('Kaydedilirken hata oluştu.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) { return <div className="flex justify-center p-20"><Loader2 className="animate-spin text-indigo-500" size={40} /></div>; }

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6 animate-in fade-in duration-500">
      <div className="flex items-center justify-between border-b border-gray-800 pb-4">
        <h1 className="text-2xl font-bold flex items-center gap-3 text-white">
          <Settings className="text-indigo-500" size={28} /> Kullanıcı Ayarları
        </h1>
        <button 
          onClick={() => void handleSave()} 
          disabled={saving}
          className="bg-indigo-600 hover:bg-indigo-500 text-white px-5 py-2 rounded-lg font-semibold flex items-center gap-2 transition-all disabled:opacity-50"
        >
          {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
          Değişiklikleri Kaydet
        </button>
      </div>

      <div className="flex flex-col md:flex-row gap-8">
        {/* Sidebar Tabs */}
        <div className="w-full md:w-64 space-y-2 shrink-0">
          <TabBtn active={activeTab === 'profile'} onClick={() => setActiveTab('profile')} icon={<User size={18}/>} label="Profil Bilgileri" />
          <TabBtn active={activeTab === 'security'} onClick={() => setActiveTab('security')} icon={<Shield size={18}/>} label="Güvenlik & Şifre" />
          <TabBtn active={activeTab === 'notifications'} onClick={() => setActiveTab('notifications')} icon={<Bell size={18}/>} label="Bildirimler" />
          <TabBtn active={activeTab === 'preferences'} onClick={() => setActiveTab('preferences')} icon={<Sliders size={18}/>} label="Tercihler" />
        </div>

        {/* Tab Content */}
        <div className="flex-1 bg-gray-900 border border-gray-800 rounded-2xl p-6 shadow-xl min-h-[400px]">
          {activeTab === 'profile' && (
            <div className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">Görünüm Adı</label>
                <input type="text" value={formData.display_name || ''} onChange={e => setFormData({...formData, display_name: e.target.value})} className="w-full bg-black/50 border border-gray-700 rounded-lg px-4 py-2.5 text-white focus:border-indigo-500 focus:outline-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">E-posta Adresi</label>
                <input type="email" value={formData.email || ''} disabled className="w-full bg-black/50 border border-gray-800 text-gray-500 rounded-lg px-4 py-2.5 cursor-not-allowed" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">Hakkında (Bio)</label>
                <textarea rows={4} value={formData.bio || ''} onChange={e => setFormData({...formData, bio: e.target.value})} className="w-full bg-black/50 border border-gray-700 rounded-lg px-4 py-2.5 text-white focus:border-indigo-500 focus:outline-none placeholder-gray-600" placeholder="Kendinizden veya uzmanlık alanınızdan bahsedin..." />
              </div>
            </div>
          )}

          {activeTab === 'security' && (
            <div className="space-y-6">
              <div className="bg-black/30 border border-gray-800 p-5 rounded-xl flex items-center justify-between">
                <div>
                  <h4 className="text-white font-medium">İki Faktörlü Doğrulama (2FA)</h4>
                  <p className="text-sm text-gray-500">Hesabınızı ekstra bir güvenlik katmanıyla koruyun.</p>
                </div>
                <Toggle checked={formData.two_factor_enabled} onChange={v => setFormData({...formData, two_factor_enabled: v})} />
              </div>
              <div className="bg-black/30 border border-gray-800 p-5 rounded-xl">
                <h4 className="text-white font-medium mb-2">Şifre Değiştir</h4>
                <p className="text-sm text-gray-500 mb-4">Şifre sıfırlama bağlantısı kayıtlı e-posta adresinize gönderilecektir.</p>
                <button className="bg-gray-800 hover:bg-gray-700 text-white px-4 py-2 rounded-lg text-sm transition-colors">Sıfırlama Bağlantısı Gönder</button>
              </div>
            </div>
          )}

          {activeTab === 'notifications' && (
            <div className="space-y-4">
              <ToggleRow label="E-posta Bildirimleri" desc="Platform güncellemeleri ve uyarılar hakkında mail alın." checked={formData.email_notifications} onChange={v => setFormData({...formData, email_notifications: v})} />
              <ToggleRow label="Pazarlama E-postaları" desc="Kampanyalar ve yeni özellik duyuruları." checked={formData.marketing_emails} onChange={v => setFormData({...formData, marketing_emails: v})} />
            </div>
          )}

          {activeTab === 'preferences' && (
            <div className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">Arayüz Teması</label>
                <select value={formData.theme || 'dark'} onChange={e => setFormData({...formData, theme: e.target.value as 'dark' | 'light'})} className="w-full bg-black/50 border border-gray-700 rounded-lg px-4 py-2.5 text-white focus:border-indigo-500 focus:outline-none">
                  <option value="dark">Karanlık Tema (Dark)</option>
                  <option value="light">Aydınlık Tema (Light)</option>
                </select>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

interface TabBtnProps {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}

const TabBtn = ({ active, onClick, icon, label }: TabBtnProps) => (
  <button onClick={onClick} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${active ? 'bg-indigo-600/10 text-indigo-400 border border-indigo-500/20' : 'text-gray-400 hover:bg-gray-800 hover:text-white border border-transparent'}`}>
    {icon} {label}
  </button>
);

interface ToggleProps {
  checked?: boolean;
  onChange: (v: boolean) => void;
}

const Toggle = ({ checked, onChange }: ToggleProps) => (
  <button onClick={() => onChange(!checked)} className={`relative w-12 h-6 rounded-full transition-colors ${checked ? 'bg-indigo-500' : 'bg-gray-700'}`}>
    <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${checked ? 'left-7' : 'left-1'}`} />
  </button>
);

interface ToggleRowProps {
  label: string;
  desc: string;
  checked?: boolean;
  onChange: (v: boolean) => void;
}

const ToggleRow = ({ label, desc, checked, onChange }: ToggleRowProps) => (
  <div className="flex items-center justify-between p-4 bg-black/30 border border-gray-800 rounded-xl">
    <div><div className="text-white font-medium text-sm">{label}</div><div className="text-gray-500 text-xs mt-0.5">{desc}</div></div>
    <Toggle checked={checked} onChange={onChange} />
  </div>
);